import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ScrollAnimation } from './ScrollAnimation';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const PageWrapper = styled.div<{ $heroImage?: string }>`
  padding: var(--spacing-section-mobile) 0;
  background: linear-gradient(180deg, 
    var(--color-primary-light) 0%, 
    var(--color-primary-base) 30%,
    var(--color-primary-light) 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      ${props => props.$heroImage ? `url('${props.$heroImage.replace('.png', '.webp')}'),` : ''}
      radial-gradient(circle at 20% 80%, rgba(11,152,206,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(49,110,168,0.04) 0%, transparent 50%);
    background-size: cover, auto, auto;
    background-position: center, 20% 80%, 80% 20%;
    background-repeat: no-repeat, no-repeat, no-repeat;
    opacity: ${props => props.$heroImage ? '0.08' : '1'};
    pointer-events: none;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(11,152,206,0.1);
  border-radius: 999px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: var(--spacing-xl);
  
  &:hover {
    background: white;
    border-color: var(--color-accent-primary);
    color: var(--color-accent-primary);
    transform: translateX(-4px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    transform: rotate(180deg);
  }
`;

const ServiceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(11,152,206,0.1);
  border: 1px solid rgba(11,152,206,0.2);
  border-radius: 999px;
  color: var(--color-accent-primary);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-lg);
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: var(--fs-5xl);
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: var(--spacing-lg);
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: var(--fs-6xl);
  }
`;

const Subtitle = styled.p`
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  line-height: var(--lh-relaxed);
`;

const HighlightStats = styled.div`
  display: inline-flex;
  gap: var(--spacing-xl);
  padding: var(--spacing-md) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 999px;
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(11,152,206,0.1);
  margin-top: var(--spacing-lg);
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
`;

const Stat = styled.div`
  text-align: center;
  
  .value {
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
    display: block;
  }
  
  .label {
    font-size: var(--fs-xs);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--ls-wider);
  }
`;

const ContentSection = styled.section`
  position: relative;
  z-index: 2;
  margin-bottom: var(--spacing-4xl);
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-3xl);
  
  h2 {
    font-size: var(--fs-3xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
  }
  
  p {
    font-size: var(--fs-lg);
    color: var(--text-secondary);
    max-width: 600px;
    margin: 0 auto;
    line-height: var(--lh-relaxed);
  }
`;

const ContentGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-xl);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(${props => props.$columns === 2 ? 2 : 1}, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(${props => props.$columns || 3}, 1fr);
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: var(--spacing-xl);
  border: 1px solid rgba(11,152,206,0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 200% 200%;
    animation: ${gradientMove} 8s ease infinite;
    border-radius: 20px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 60px rgba(11,152,206,0.15);
    
    &::before {
      opacity: 0.3;
    }
    
    .icon {
      animation: ${float} 2s ease-in-out infinite;
    }
  }
  
  .icon {
    width: 48px;
    height: 48px;
    color: var(--color-accent-primary);
    margin-bottom: var(--spacing-lg);
    transition: all 0.3s ease;
  }
  
  h3 {
    font-size: var(--fs-xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
    font-weight: var(--font-weight-bold);
  }
  
  p {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
    margin-bottom: var(--spacing-md);
  }
  
  .benefits {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      color: var(--text-secondary);
      font-size: var(--fs-sm);
      margin-bottom: var(--spacing-xs);
      
      &:before {
        content: '✓';
        color: var(--color-accent-primary);
        font-weight: var(--font-weight-bold);
        font-size: var(--fs-md);
      }
    }
  }
`;

const ProcessSection = styled.section`
  position: relative;
  z-index: 2;
  margin-bottom: var(--spacing-4xl);
`;

const ProcessTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
  max-width: 800px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    gap: var(--spacing-xl);
  }
`;

const ProcessStep = styled.div<{ $index: number }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.$index * 0.1}s;
  animation-fill-mode: both;
  
  @media (max-width: 767px) {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xl);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(11,152,206,0.3);
`;

const StepContent = styled.div`
  flex: 1;
  
  h3 {
    font-size: var(--fs-xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-sm);
    font-weight: var(--font-weight-bold);
  }
  
  p {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
  }
`;

const CTASection = styled.section`
  text-align: center;
  padding: var(--spacing-3xl) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(11,152,206,0.02) 0%, 
      rgba(49,110,168,0.03) 100%);
    pointer-events: none;
  }
  
  h2 {
    font-size: var(--fs-2xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
    position: relative;
    z-index: 1;
  }
  
  p {
    font-size: var(--fs-lg);
    color: var(--text-secondary);
    margin-bottom: var(--spacing-xl);
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
  }
`;

const CTAButton = styled.button`
  padding: var(--spacing-md) var(--spacing-2xl);
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  color: white;
  border: none;
  border-radius: 12px;
  font-size: var(--fs-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(11,152,206,0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

// Interface
interface ServiceLayoutProps {
  serviceName: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  heroImage?: string;
  stats?: Array<{ value: string; label: string }>;
  features: Array<{
    icon: React.ReactNode;
    title: string;
    description: string;
    benefits?: string[];
  }>;
  process?: {
    title: string;
    description: string;
    steps: Array<{ title: string; description: string }>;
  };
  cta: {
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  };
  children?: React.ReactNode;
}

const ServiceLayout: React.FC<ServiceLayoutProps> = ({
  serviceName,
  icon,
  title,
  subtitle,
  heroImage,
  stats,
  features,
  process,
  cta,
  children
}) => {
  const navigate = useNavigate();
  
  const handleBackClick = () => {
    navigate('/services');
  };

  return (
    <PageWrapper $heroImage={heroImage}>
      <div className="container">
        <HeroSection>
          <ScrollAnimation animation="fadeIn" delay={100}>
            <BackLink onClick={handleBackClick}>
              <ArrowRightIcon />
              Back to Services
            </BackLink>
            
            <ServiceBadge>
              {icon}
              {serviceName}
            </ServiceBadge>
            
            <Title>{title}</Title>
            <Subtitle>{subtitle}</Subtitle>
            
            {stats && (
              <HighlightStats>
                {stats.map((stat, index) => (
                  <Stat key={index}>
                    <span className="value">{stat.value}</span>
                    <span className="label">{stat.label}</span>
                  </Stat>
                ))}
              </HighlightStats>
            )}
          </ScrollAnimation>
        </HeroSection>

        <ContentSection>
          <ScrollAnimation animation="slideInFromBottom" delay={200}>
            <SectionHeader>
              <h2>What We Offer</h2>
              <p>Comprehensive solutions tailored to your business needs</p>
            </SectionHeader>
          </ScrollAnimation>
          
          <ContentGrid $columns={features.length === 2 ? 2 : 3}>
            {features.map((feature, index) => (
              <ScrollAnimation key={index} animation="fadeIn" delay={300 + index * 100}>
                <FeatureCard>
                  <div className="icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  {feature.benefits && (
                    <ul className="benefits">
                      {feature.benefits.map((benefit, i) => (
                        <li key={i}>{benefit}</li>
                      ))}
                    </ul>
                  )}
                </FeatureCard>
              </ScrollAnimation>
            ))}
          </ContentGrid>
        </ContentSection>

        {process && (
          <ProcessSection>
            <ScrollAnimation animation="slideInFromBottom" delay={100}>
              <SectionHeader>
                <h2>{process.title}</h2>
                <p>{process.description}</p>
              </SectionHeader>
            </ScrollAnimation>
            
            <ProcessTimeline>
              {process.steps.map((step, index) => (
                <ProcessStep key={index} $index={index}>
                  <StepNumber>{index + 1}</StepNumber>
                  <StepContent>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </StepContent>
                </ProcessStep>
              ))}
            </ProcessTimeline>
          </ProcessSection>
        )}

        {children}

        <ScrollAnimation animation="scaleIn" delay={100}>
          <CTASection>
            <h2>{cta.title}</h2>
            <p>{cta.description}</p>
            <CTAButton onClick={cta.onAction}>
              {cta.buttonText}
              <ArrowRightIcon />
            </CTAButton>
          </CTASection>
        </ScrollAnimation>
      </div>
    </PageWrapper>
  );
};

export default ServiceLayout;